// ─── Notification Types ───
// Extensible notification system - add new types to NotificationType as needed.
import { encodeProfileId } from '../../services/apiService';

export enum NotificationType {
  FOLLOW = 'FOLLOW',
  COIN_TRANSFER = 'COIN_TRANSFER',
  CHALLENGE_JOIN = 'CHALLENGE_JOIN',
  CHALLENGE_RESULT = 'CHALLENGE_RESULT',
  /**
   * Server / admin-originated message sent under the
   * {@code notifications@bookiebrawler.com} actor. The {@code message}
   * field is the literal body the admin typed (capped at
   * {@link SYSTEM_NOTIFICATION_MAX_LEN} characters server-side).
   */
  SYSTEM = 'SYSTEM',
  /** Someone liked your feed post — `post_id` deep-links to it. */
  LIKE = 'LIKE',
  /** Someone replied to your feed post — `post_id` is the reply. */
  REPLY = 'REPLY',
  /** Someone @mentioned you in a feed post — `post_id` is that post. */
  MENTION = 'MENTION',
  /** Someone reposted your feed post — `post_id` is your original. */
  REPOST = 'REPOST',
  /** Someone quoted your feed post — `post_id` is the quote. */
  QUOTE = 'QUOTE',
  /** Someone requested to join a PRIVATE community you moderate — `community_id`. */
  COMMUNITY_JOIN_REQUEST = 'COMMUNITY_JOIN_REQUEST',
  /** Your request to join a community was approved — `community_id`. */
  COMMUNITY_JOIN_APPROVED = 'COMMUNITY_JOIN_APPROVED',
}

/**
 * Hard cap on a SYSTEM notification body. Mirrors
 * {@code SystemNotificationService.MAX_MESSAGE_LENGTH} on the backend.
 * Anything longer is rejected at the API boundary, so we use the same
 * value to truncate defensively if a stale row sneaks through.
 */
export const SYSTEM_NOTIFICATION_MAX_LEN = 280;

/** The actor who triggered the notification */
export interface NotificationActor {
  id: number;
  handle?: string;
  firstname?: string;
  lastname?: string;
  username?: string;
  picture?: string;
  affiliated?: boolean;
}

/** A single notification from the API */
export interface AppNotification {
  id: number;
  type: NotificationType;
  actor: NotificationActor;
  message?: string;        // optional server-generated message
  /**
   * Optional headline set by the notification delivery system (SYSTEM
   * rows sent via NotificationDispatcher). Legacy social rows leave it null.
   */
  title?: string | null;
  /**
   * Optional explicit internal route ("/coins") from the delivery system.
   * When present it wins over the type-based routing below; validated as
   * internal server-side, and re-checked client-side before navigating.
   */
  deep_link?: string | null;
  /** Feed-post reference for LIKE / REPLY / MENTION deep links */
  post_id?: number | null;
  /** Community reference for COMMUNITY_JOIN_* deep links */
  community_id?: number | null;
  read: boolean;
  created_at: string;      // ISO date string
}

/**
 * Client-side twin of the backend's deep-link validation: internal route
 * only — single leading "/", no "//", no backslashes. Defense in depth;
 * anything else is ignored by the panel.
 */
export function isInternalDeepLink(link: string | null | undefined): link is string {
  return (
    typeof link === 'string' &&
    link.length > 0 &&
    link[0] === '/' &&
    !link.startsWith('//') &&
    !link.includes('\\')
  );
}

/**
 * Checks if a string looks like a default/auto-generated username.
 * Default format: email + UUID like "useremmanuel.mcmanus@gmail.comd3711230-ceb9-4a14-a56c-becc5c5326b4"
 */
function isDefaultHandle(str: string): boolean {
  if (!str) return true;
  const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
  const emailPattern = /@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  return uuidPattern.test(str) || (emailPattern.test(str) && str.length > 30);
}

/** Helper to build a display name from the actor */
export function getActorDisplayName(actor: NotificationActor): string {
  if (actor.handle && !isDefaultHandle(actor.handle)) return `@${actor.handle}`;
  if (actor.firstname && actor.lastname) return `${actor.firstname} ${actor.lastname}`;
  if (actor.firstname) return actor.firstname;
  if (actor.username && !isDefaultHandle(actor.username)) return actor.username;
  return 'Someone';
}

/**
 * Parse a challenge notification message.
 * CHALLENGE_JOIN format:  bracketName|challengeName|inviteCode
 * CHALLENGE_RESULT format: rank|challengeName|bracketName|inviteCode
 */
export function parseChallengeMessage(type: NotificationType, message?: string) {
  if (!message) return null;
  const parts = message.split('|');
  if (type === NotificationType.CHALLENGE_JOIN && parts.length >= 3) {
    return { bracketName: parts[0], challengeName: parts[1], inviteCode: parts[2] };
  }
  if (type === NotificationType.CHALLENGE_RESULT && parts.length >= 4) {
    return { rank: parseInt(parts[0], 10), challengeName: parts[1], bracketName: parts[2], inviteCode: parts[3] };
  }
  return null;
}

/**
 * Defensive trim for a SYSTEM message body. The server already caps the
 * length at {@link SYSTEM_NOTIFICATION_MAX_LEN}, but a stale row from
 * before the cap was added (or a bug elsewhere) shouldn't blow out our
 * notification row. Falls back to a generic line if the body is empty.
 */
export function getSystemMessageBody(message?: string): string {
  const trimmed = (message ?? '').trim();
  if (!trimmed) return 'New update from Bookie Brawler';
  if (trimmed.length <= SYSTEM_NOTIFICATION_MAX_LEN) return trimmed;
  return trimmed.slice(0, SYSTEM_NOTIFICATION_MAX_LEN - 1).trimEnd() + '…';
}

/** Human-friendly label per notification type */
export function getNotificationLabel(type: NotificationType, message?: string): string {
  switch (type) {
    case NotificationType.FOLLOW:
      return message === 'FOLLOW_BACK' ? 'followed you back' : 'started following you';
    case NotificationType.COIN_TRANSFER:
      // Backend message format: "@handle sent you 100 Brawler Coins - note"
      // Strip the "@handle " prefix and the trailing note/comment
      if (message) {
        const match = message.match(/sent you (\d[\d,]*) Brawler Coins/);
        if (match) {
          return `sent you ${match[1]} Brawler Coins`;
        }
      }
      return 'sent you Brawler Coins';
    case NotificationType.CHALLENGE_JOIN: {
      const parsed = parseChallengeMessage(type, message);
      if (parsed && 'bracketName' in parsed && 'challengeName' in parsed) {
        return `joined "${parsed.challengeName}" with bracket "${parsed.bracketName}"`;
      }
      return 'joined your challenge';
    }
    case NotificationType.CHALLENGE_RESULT: {
      const parsed = parseChallengeMessage(type, message);
      if (parsed && 'rank' in parsed && 'challengeName' in parsed) {
        const rank = (parsed as any).rank as number;
        const suffix = rank === 1 ? 'st' : rank === 2 ? 'nd' : rank === 3 ? 'rd' : 'th';
        return `You placed ${rank}${suffix} in "${parsed.challengeName}"`;
      }
      return 'Your challenge results are in!';
    }
    case NotificationType.LIKE:
      return 'liked your post';
    case NotificationType.REPLY:
      return 'replied to your post';
    case NotificationType.MENTION:
      return 'mentioned you in a post';
    case NotificationType.REPOST:
      return 'reposted your post';
    case NotificationType.QUOTE:
      return 'quoted your post';
    case NotificationType.COMMUNITY_JOIN_REQUEST:
      return 'requested to join a community you moderate';
    case NotificationType.COMMUNITY_JOIN_APPROVED:
      return 'approved your request to join their community';
    case NotificationType.SYSTEM:
      // For SYSTEM the entire message body is the payload — render it
      // verbatim instead of a generic action line. UI also uses this
      // path (NotificationItem) to lay out actor + body on two rows.
      return getSystemMessageBody(message);
    default:
      return 'sent you a notification';
  }
}

/** Get a profile URL for a notification actor */
export function getActorProfileUrl(actor: NotificationActor): string | null {
  if (actor.handle && !isDefaultHandle(actor.handle)) {
    return `/profile/${actor.handle}`;
  }
  if (actor.id) {
    return `/profile/${encodeProfileId(actor.id)}`;
  }
  return null;
}
